import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { shareBoardWithTeam } from '../../records/boardTeamMove';
import teamsService from '../../services/teamsService';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { setTeamLibraryRefresh } from '../../store/teamsStore';
import { useNotificationStore } from '../../store/notificationStore';

// Why a share failed, in words the user can act on.
function reasonText(err) {
  const status = err?.response?.status;
  const reason = String(err?.message || err || '');
  if (status === 403) return 'You need editor rights in this team to share a board with it.';
  if (status === 404) return 'The board was not found on the server. Wait for sync to finish and try again.';
  if (err?.response?.data?.error === 'scope_limit_reached') return 'This team has reached its record limit.';
  if (reason === 'pending_changes') return 'Your latest edits have not synced yet. Check your connection and try again.';
  if (reason === 'offline' || err?.code === 'ERR_NETWORK') return 'You are offline. Sharing needs a connection.';
  if (reason.startsWith('plan_')) return 'Your plan has no room for another cloud board.';
  return reason || 'Unknown error';
}

/**
 * "Share board with team" (split out of TaskBoardTab, 30.09.2026). Stays open
 * with a spinner while the move runs and says what went wrong when it fails —
 * the old handler closed first and only logged errors, so a failed share
 * looked like "nothing happens".
 */
export default function ShareBoardModal({ target, teams, onChange, onClose, onShared }) {
  const [busy, setBusy] = useState(false);
  const { id, name, teamId, onDevice } = target;

  const confirm = async () => {
    if (!teamId || busy) return;
    setBusy(true);
    try {
      await shareBoardWithTeam(id, teamId, { onDevice });
      notifyMcpSurfaceChanged('boards');
      try {
        await teamsService.shareBoardWithTeam(id, { team_id: Number(teamId) || teamId });
      } catch (err) {
        console.warn('[task-manager] shareBoardWithTeam failed (board still moved):', err);
      }
      setTeamLibraryRefresh({ teamId: Number(teamId) || teamId, ts: Date.now() });
      const team = teams.find((t) => String(t.id) === String(teamId));
      useNotificationStore.getState().success(`“${name}” is now shared with ${team?.name || 'the team'}.`, 'Board shared');
      onShared();
    } catch (err) {
      console.warn('[task-manager] share board failed:', err);
      useNotificationStore.getState().error(reasonText(err), 'Could not share the board');
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="Share board with team">
      <div className="flex flex-col" style={{ gap: '0.875rem' }} aria-busy={busy}>
        <p className="task-board-setting-hint">
          “{name}” will move to the team and become realtime for all members.
          You can make it private again later.
        </p>
        <div>
          <label className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
            Team
          </label>
          <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
            <CompactSelectPicker
              value={teamId}
              onChange={(v) => onChange({ ...target, teamId: v })}
              options={teams.map((t) => ({ value: String(t.id), label: t.name }))}
              aria-label="Team"
              disabled={busy}
            />
          </div>
        </div>
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!teamId || busy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {busy ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
